import { Account } from '@/domain/usecases'
import { RouteObject } from 'react-router-dom'

// Componentes de layout
import BasicLayout from '@/presentation/components/ui/layout/basic-layout'
import LoggedLayout from '@/presentation/components/ui/layout/logged-layout'

import * as Loaders from '../../data-loaders'
import { createLoaderFactory, LoaderContext } from '../../shared/loaders'
import { PathConstants } from '../../paths'
import * as Pages from './pages'
import { backofficeGuards } from './guards'

// ============================================
// ROTAS DO MÓDULO BACKOFFICE
// Fluxos administrativos para companyManager, internalCompanyManager (superAdmin)
// e systemManager. Todos os paths são servidos sob /backoffice/*.
//
// companyManager e internalCompanyManager compartilham o mesmo layout admin,
// então ficam em um único grupo de rotas. O React Router prioriza segmentos
// estáticos sobre dinâmicos, então `/backoffice/user/batch-disable` casa antes
// de `/backoffice/user/:accountId/profile`, independentemente da ordem de declaração.
// ============================================

export const BackofficeRoutes = (account: Account.Model): RouteObject[] => {
  const routeLoader = createLoaderFactory(account)

  return [
    // ========================================
    // ROTAS ADMIN (companyManager + internalCompanyManager)
    // Layout: LoggedLayout (navMenu="admin", footerVariant="compact", enableSearch=false)
    // ========================================
    {
      element: <LoggedLayout navMenu="admin" footerVariant="compact" enableSearch={false} />,
      children: [
        // ----- Company Manager -----
        {
          path: PathConstants.Routes.COMPANY_MANAGER_DASHBOARD.value,
          element: <Pages.DashboardPage />,
          loader: routeLoader(backofficeGuards.companyManager, () =>
            Loaders.loadCompanyManagerDashboard()
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_CREATE_USER_PROFILE.value,
          element: <Pages.CreateUserProfilePage />,
          loader: routeLoader(backofficeGuards.anyCompanyManager, () =>
            Loaders.loadCompanyManagerCreateUserProfile(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_EDIT_USER_PROFILE.value,
          element: <Pages.EditUserProfilePage />,
          loader: routeLoader(backofficeGuards.anyCompanyManager, ({ params }: LoaderContext) =>
            Loaders.loadCompanyManagerUserProfile(params!.accountId!, account.id)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE.value,
          element: <Pages.EditUserProfilePage />,
          loader: routeLoader(backofficeGuards.anyCompanyManager, ({ params }: LoaderContext) =>
            Loaders.loadCompanyManagerUserProfile(params!.accountId!, account.id)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_DEPENDENT_LIST.value,
          element: <Pages.DependentSearchPage />,
          loader: routeLoader(backofficeGuards.companyManager, ({ request }: LoaderContext) =>
            Loaders.loadCompanyManagerDependentSearch(request!)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_HOLDER_SEARCH.value,
          element: <Pages.DependentHolderSearchPage accountProfile={account} />,
          loader: routeLoader(backofficeGuards.companyManager, () =>
            Loaders.loadCompanyManagerDependentLicenses(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_DEPENDENT_REGISTRATION.value,
          element: <Pages.DependentRegistrationPage />,
          loader: routeLoader(backofficeGuards.companyManager, ({ params }: LoaderContext) =>
            Loaders.loadCompanyManagerDependentRegistration(params!.accountId!)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_REQUESTS_CONFIGURATION.value,
          element: <Pages.RequestsConfigurationPage />,
          loader: routeLoader(backofficeGuards.companyManager, () =>
            Loaders.loadCompanyManagerDependentConfiguration(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_DEPENDENT_ENROLLMENT.value,
          element: <Pages.DependentEnrollmentPage />,
          loader: routeLoader(backofficeGuards.companyManager, () =>
            Loaders.loadCompanyManagerDependentConfiguration(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_UPGRADE_ENROLLMENT.value,
          element: <Pages.UpgradeEnrollmentPage />,
          loader: routeLoader(backofficeGuards.companyManager, () =>
            Loaders.loadCompanyManagerUpgradeRequest(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_SCHOLARSHIP_RENEWAL.value,
          element: <Pages.ScholarshipRenewalPage />,
          // Sem dataFn: a listagem é carregada client-side via React Query.
          // O loader existe apenas para aplicar o guard de persona.
          loader: routeLoader(backofficeGuards.companyManager, null)
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_AUTHORIZATIONS.value,
          element: <Pages.AuthorizationPage />,
          loader: routeLoader(backofficeGuards.anyCompanyManager, () =>
            Loaders.loadCompanyManagerAuthorizations(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_AUTHORIZATION_DETAIL.value,
          element: <Pages.AuthorizationDetailPage />,
          loader: routeLoader(backofficeGuards.anyCompanyManager, ({ params }: LoaderContext) =>
            Loaders.loadAuthorizationDetail(params!.authorizationId!)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_COLLABORATORS.value,
          element: <Pages.CollaboratorsPage />,
          loader: routeLoader(backofficeGuards.anyCompanyManager, () =>
            Loaders.loadCompanyManagerCollaborators(account)
          )
        },
        {
          path: PathConstants.Routes.COMPANY_MANAGER_GROUPS.value,
          element: <Pages.GroupsPage />,
          loader: routeLoader(backofficeGuards.anyCompanyManager, () =>
            Loaders.loadCompanyManagerGroups(account)
          )
        },

        // ----- Internal Company Manager -----
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_USER_BATCH_DISABLE.value,
          element: <Pages.BatchDisableUserPage />,
          loader: routeLoader(backofficeGuards.internalCompanyManager, null)
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_COMPANY_CREATE.value,
          element: <Pages.CompanyCreatePage />,
          loader: routeLoader(backofficeGuards.internalCompanyManager, null)
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_INVOICE.value,
          element: <Pages.InvoicePage />,
          loader: routeLoader(
            backofficeGuards.internalCompanyManager,
            ({ request }: LoaderContext) => Loaders.loadInvoiceList(request!)
          )
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_INVOICE_DETAIL.value,
          element: <Pages.InvoiceDetailPage />,
          loader: routeLoader(
            backofficeGuards.internalCompanyManager,
            ({ params }: LoaderContext) => Loaders.loadInvoiceDetail(params!.id!)
          )
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_COMPANIES.value,
          element: <Pages.CompaniesListPage />,
          loader: routeLoader(backofficeGuards.internalCompanyManager, null)
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_COMPANY_DETAIL.value,
          element: <Pages.CompanyDetailPage />,
          // Acesso: RH + SuperAdmin (guard genérico); a tela lida com as regras por papel.
          loader: routeLoader(
            backofficeGuards.anyCompanyManager,
            ({ params }: LoaderContext) => Loaders.loadCompanyDetail(params?.id ?? ''),
            { blocking: true }
          ),
          // A aba ativa mora na URL (?tab=) pra ser recarregável/compartilhável. Trocar de
          // aba só muda a query, então pulamos a revalidação do loader (que recarregaria a
          // página); revalida só quando o pathname muda (entrar de fato em outra empresa).
          shouldRevalidate: ({ currentUrl, nextUrl }) => currentUrl.pathname !== nextUrl.pathname
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_PARTNER_DETAIL.value,
          element: <Pages.PartnerDetailPage />,
          loader: routeLoader(
            backofficeGuards.internalCompanyManager,
            ({ params }: LoaderContext) => Loaders.loadPartnerDetail(params!.id!)
          ),
          // A aba ativa mora na URL (?tab=) pra ser recarregável/compartilhável. Trocar de
          // aba só muda a query, então pulamos a revalidação do loader (que recarregaria a
          // página); revalida só quando o pathname muda (entrar de fato em outro parceiro).
          shouldRevalidate: ({ currentUrl, nextUrl }) => currentUrl.pathname !== nextUrl.pathname
        },
        {
          path: PathConstants.Routes.INTERNAL_COMPANY_MANAGER_FIT_SCORE.value,
          element: <Pages.FitScorePage />,
          loader: routeLoader(backofficeGuards.internalCompanyManager, null)
        }
      ]
    },

    // ========================================
    // ROTAS DE SYSTEM MANAGER
    // Layout: BasicLayout, footer compacto (sem opções de navegação)
    // ========================================
    {
      element: <BasicLayout footerVariant="compact" />,
      children: [
        {
          path: PathConstants.Routes.SYSTEM_MANAGER_CONFIGURATIONS.value,
          element: <Pages.SystemConfigurationsPage />,
          loader: routeLoader(backofficeGuards.systemManager, () =>
            Loaders.loadSystemConfigurations()
          )
        }
      ]
    }
  ]
}
